import * as React from 'react';
import { CarouselSkipLink } from '../../../CarouselSkipLink';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Navigation, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import useAppState from '../../../../common/state/appState';
import { useCallback } from 'react';

interface ICarouselProps {
    children: React.ReactElement[];
    name: string;
    nextFocus: string;
    ariaLabel?: string;
    leftAligned?: boolean;
    disableNavButtons?: boolean;
    onSwiper?: (swiper: SwiperType) => void;
    onSlideChange?: (swiper: SwiperType) => void;
    navigationParams?: {
        enabled: boolean;
        nextEl: HTMLElement | null;
        prevEl: HTMLElement | null;
    };
    onBeforeInit?: () => void;
}

const StyledSwiper = styled(Swiper)(({ theme }) => {
    if (window.featureFlags.enableUFTHome) {
        return {
            border: 'none',
            height: '100%',
            whiteSpace: 'nowrap',
            '& .carousel': {
                marginLeft: 5,
            },
            '.swiper': {
                paddingBottom: '4px',
            },
            '& > .swiper-wrapper > .swiper-slide': {
                [theme.breakpoints.up('md')]: {
                    width: 'calc((100% - (4 * 12px)) / 5)',
                },
                [theme.breakpoints.down('md')]: {
                    width: '165px !important',
                },
            },
        };
    } else {
        return {
            height: '100%',
            '& > .swiper-wrapper > .swiper-slide': {
                [theme.breakpoints.up('md')]: {
                    width: '270px !important',
                },
                [theme.breakpoints.down('md')]: {
                    width: 'min(270px, calc(100vw - 2rem)) !important',
                },
            },
        };
    }
});

const SLIDES_OFFSET_BEFORE_MD = 8;
const SPACING = 16;

const Carousel = (props: ICarouselProps) => {
    const { children, name, nextFocus, leftAligned, onSwiper, onSlideChange, navigationParams, onBeforeInit } = props;
    const {
        breakpoints: { values: breakpoints },
    } = useTheme();
    const theme = useTheme();
    const isUftEnabled = useAppState((state) => state.serveUFTContent);

    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobileViewTablet = useMediaQuery(theme.breakpoints.down('md'));

    const returnSwiperStyling = useCallback(() => {
        if (!isUftEnabled) {
            return { overflow: 'visible', paddingLeft: 0 };
        }
        if (isMobileViewTablet) {
            return { overflow: 'visible', pb: '0 !important' };
        }
        return { pb: '0 !important' };
    }, [isMobileViewTablet, isUftEnabled]);

    return (
        <>
            <CarouselSkipLink
                id={`pre-${name}-carousel`}
                href={nextFocus}
                text={`Skip ${name.replace(/-/g, ' ')} Carousel`}
            />

            <StyledSwiper
                slidesPerView="auto"
                slidesPerGroup={isUftEnabled ? undefined : 1}
                slidesPerGroupAuto
                speed={750}
                spaceBetween={isUftEnabled ? (isMobileView ? 8 : 12) : SPACING}
                sx={returnSwiperStyling()}
                breakpoints={
                    leftAligned
                        ? {}
                        : {
                              [breakpoints.md]: {
                                  slidesOffsetBefore: SLIDES_OFFSET_BEFORE_MD,
                              },
                          }
                }
                grabCursor
                keyboard
                watchSlidesProgress
                navigation={
                    navigationParams || {
                        nextEl: `#${name}-carousel__next`,
                        prevEl: `#${name}-carousel__prev`,
                    }
                }
                onSwiper={onSwiper}
                onSlideChange={onSlideChange}
                onBeforeInit={onBeforeInit}
                modules={[Navigation, Keyboard, Mousewheel]}
            >
                {React.Children.map(children, (child: React.ReactElement) => (
                    <SwiperSlide key={`swiper-${child.key}`}>{child}</SwiperSlide>
                ))}
            </StyledSwiper>
        </>
    );
};

export default Carousel;
